const RECOVERY_TIMESTAMP_KEY = '__chunk_load_recovery__';
const RECOVERY_QUERY_PARAM = '__chunk-reload__';
const RECOVERY_COOLDOWN_MS = 30_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk [\w-]+ failed/i,
];

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (e.g. private browsing restrictions)
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }

  return typeof error === 'string' ? error : '';
}

export function isDynamicImportFailure(error: unknown): boolean {
  const message = getErrorMessage(error);
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function clearChunkRecoveryParam(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has(RECOVERY_QUERY_PARAM)) return;

  url.searchParams.delete(RECOVERY_QUERY_PARAM);
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, document.title, nextUrl);
}

export async function recoverFromChunkLoadError(force = false): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const lastRecovery = safeSessionGet(RECOVERY_TIMESTAMP_KEY);

  if (!force && lastRecovery && now - Number(lastRecovery) < RECOVERY_COOLDOWN_MS) {
    return false;
  }

  safeSessionSet(RECOVERY_TIMESTAMP_KEY, String(now));

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(registrations.map((registration) => registration.unregister()));
    } catch {
      // Ignore service worker cleanup failures
    }
  }

  if ('caches' in window) {
    try {
      const cacheKeys = await caches.keys();
      await Promise.allSettled(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    } catch {
      // Ignore cache cleanup failures
    }
  }

  const recoveryUrl = new URL(window.location.href);
  recoveryUrl.searchParams.set(RECOVERY_QUERY_PARAM, String(now));
  window.location.replace(recoveryUrl.toString());

  return true;
}